import ProductCatalog from "../domains2/products/ProductCatalog";
import appConfig from "../appconfig";
import { BifrostBackendApi, BifrostBackendNestApi, BifrostBackendViaCdnApi, BifrostBackendViaCdnApiV2 } from "../gateways/BifrostBackendApi";
import { flowSetting } from "../libraries/utils";
import { store } from "../store";

const isNestBackend = () => store && store.getters.featureToggles && store.getters.featureToggles.useNestOrderPaymentFlow;

const getApiWithCdn = (useNestOrderPaymentFlow) => useNestOrderPaymentFlow ? BifrostBackendViaCdnApiV2 : BifrostBackendViaCdnApi;
const getApi = (useNestOrderPaymentFlow) => useNestOrderPaymentFlow ? BifrostBackendNestApi : BifrostBackendApi;

const getProductCatalogApi = (useNewProductCatalog = false, useApiCdnCaching = false, useNestOrderPaymentFlow = isNestBackend()) => {
	if (useApiCdnCaching && !flowSetting.isFlow("admin")) {
		return useNewProductCatalog ? BifrostBackendViaCdnApiV2 : getApiWithCdn(useNestOrderPaymentFlow);
	}
	return useNewProductCatalog ? BifrostBackendNestApi : getApi(useNestOrderPaymentFlow);
};

/**
 * import {getVersion} from "../helper/ProductHelper";
 * For some reason compile time increase too much when import from "../helper/ProductHelper";
 * on mac OS to ~150 seconds, and on ubuntu to ~360+ seconds...
 * @returns
 */
const getVersion = () => {
	return sessionStorage.getItem("version");
};

/**
	Fixing an error with different field names for feathersjs and nestjs
*/
const mapProductGroupPayload = (groupData) => {
	if (
		!groupData ||
		!Object.hasOwn(groupData, "subProductData") ||
		!isNestBackend()
	) {
		return groupData;
	}

	const { subProductData, subProductIds, ...rest } = groupData;

	const subProductsData = (subProductIds || []).map((id) => ({
		id,
		data: {
			productPriceInGroup:
				subProductData?.[id]?.productPriceInGroup ?? undefined,
			additionalDetails: subProductData?.[id]?.additionalDetails ?? {}
		}
	}));

	return {
		...rest,
		subProductsData
	};
};

const ProductCatalogProvider = Object.freeze({
	async getProductCatalogData({
		versionId = null,
		useNewProductCatalog = false,
		useApiCdnCaching = false,
		shopId = null,
		deliveryMethod = null,
		include = null,
		useNestOrderPaymentFlow = isNestBackend()
	}) {
		const isAdmin = flowSetting.isFlow("admin");
		const productCatalogUrl = useNewProductCatalog
			? `/intusr/productCatalog/v-${getVersion() === "next" ? "next" : "current" }`
			: "/product-catalog";
		const productCatalogHeaders = {
			"x-logging-req-start-timestamp": Date.now()
		};

		if (useNewProductCatalog) {
			productCatalogHeaders["X-TenantId"] = appConfig.tenantId;
		}

		const productCatalogParams = {
			tenantId: appConfig.tenantId,
			forceRefresh: isAdmin,
			clearRedisCache: !isAdmin,
			...(versionId ? {versionId} : {}),
			...(isAdmin ? {includeDeactivatedProducts: true}: {})
		};

		const newProductCatalogParams = {
			...(shopId ? {shopId} : {}),
			...(deliveryMethod !== null ? {deliveryMethod} : {}),
			...(include ? {include} : {}),
			...(isAdmin ? {includeDeactivatedProducts: true}: {})
		};
		const productCatalogData = await getProductCatalogApi(useNewProductCatalog, useApiCdnCaching, useNestOrderPaymentFlow).get(
			productCatalogUrl,
			{
				headers: productCatalogHeaders,
				params: useNewProductCatalog ? newProductCatalogParams : productCatalogParams
			});

		if (useNewProductCatalog) {
			return {...productCatalogData.data.productCatalog, priceRules: productCatalogData.data.operands.priceRules };
		}

		return productCatalogData.data;
	},

	async refreshProductCatalog(jwtToken) {
		return getProductCatalogApi().post(`/product-catalog/clear-cache?tenantId=${appConfig.tenantId}`, {}, {
			headers: {
				Authorization: jwtToken
			}
		});
	},

	async getProductCatalog(versionId = null, useNewProductCatalog = false, useApiCdnCaching = false, useNestOrderPaymentFlow = isNestBackend()) {
		const data = await ProductCatalogProvider.getProductCatalogData({
			versionId,
			useNewProductCatalog,
			useApiCdnCaching,
			useNestOrderPaymentFlow
		});
		return new ProductCatalog(data);
	},

	async createProduct(productData, jwtToken, useNewProductCatalog = false) {
		const response = await getProductCatalogApi(useNewProductCatalog).post(`/products`, productData, {
			headers: {
				Authorization: jwtToken
			}
		});

		return response.data;
	},

	async duplicateProduct(productId, jwtToken, versionId = 1, useNewProductCatalog = false) {
		const response = await getProductCatalogApi(useNewProductCatalog).post(
			`/products/${productId}/duplicate?versionId=${versionId}&nameAsCopy=true`,
			{},
			{
				headers: {
					Authorization: jwtToken
				}
			}
		);

		return response.data;
	},

	updateProduct(product, jwtToken, useNewProductCatalog = false, useNestOrderPaymentFlow = isNestBackend()) {
		if (useNewProductCatalog || useNestOrderPaymentFlow) {
			const queryParams = product.versionId ? `?versionId=${product.versionId}` : "";

			const { priceInGroup, ...productData } = product;

			const data = {
				productData: {
					...productData,
					tenantId: appConfig.tenantId,
					priceStructure: {
						"defaultPrice": product.price,
						"priceInGroup": priceInGroup
					}
				}
			};

			return getProductCatalogApi(useNewProductCatalog).patch(`/products/${product.id}${queryParams}`, data, {
				headers: {
					Authorization: jwtToken
				}
			});
		}

		return getProductCatalogApi(useNewProductCatalog).put(`/products/${product.id}`, product, {
			headers: {
				Authorization: jwtToken
			}
		});
	},

	updateProductImages(product, jwtToken, useNewProductCatalog = false, useNestOrderPaymentFlow = isNestBackend()) {
		const queryParams = (useNewProductCatalog || useNestOrderPaymentFlow) && product.versionId ? `?versionId=${product.versionId}` : "";
		const data = (useNewProductCatalog || useNestOrderPaymentFlow) ? { productData: { ...product }} : product;
		return getProductCatalogApi(useNewProductCatalog).patch(`/products/${product.id}${queryParams}`, data, {
			headers: {
				Authorization: jwtToken
			}
		});
	},

	removeProduct(productId, jwtToken, useNewProductCatalog = false, useNestOrderPaymentFlow = isNestBackend()) {
		if (useNewProductCatalog || useNestOrderPaymentFlow) {
			return getProductCatalogApi(useNewProductCatalog).post(`/products/${productId}/lifeCycle/_archive`, undefined, {
				headers: {
					Authorization: jwtToken
				}
			});
		}

		return getProductCatalogApi(useNewProductCatalog).delete(`/products/${productId}`, {
			headers: {
				Authorization: jwtToken
			}
		});
	},

	async addCatalog(catalog, jwtToken, useNewProductCatalog = false) {
		const res = await getProductCatalogApi(useNewProductCatalog).post(`/catalog`, catalog, {
			headers: {
				Authorization: jwtToken
			}
		});

		return res.data;
	},

	async updateCatalog(catalog, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).put(`/catalog/${catalog.id}`, catalog, {
			headers: {
				Authorization: jwtToken
			}
		});
	},

	async removeCatalog(catalogId, versionId, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).delete(`/catalog/${catalogId}?versionId=${versionId}`, {
			headers: {
				Authorization: jwtToken
			}
		});
	},

	insertProductGroup(groupData, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).post(
			`/product-groups`,
			mapProductGroupPayload(groupData),
			{
				headers: {
					Authorization: jwtToken
				}
			});
	},

	getAllProductGroups(jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).get(`/product-groups`,
			{
				headers: {
					Authorization: jwtToken
				}
			});
	},

	updateProductGroup({groupId, groupData}, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).put(
			`/product-groups/${groupId}`,
			mapProductGroupPayload(groupData),
			{
				headers: {
					Authorization: jwtToken
				}
			});
	},

	toggleProductGroupDisabledState({groupId, versionId, disabledAt}, jwtToken, useNewProductCatalog = false) {
		const url = isNestBackend()
			? `/product-groups/${groupId}/disabled-state?versionId=${versionId}`
			: `/product-groups-disabled-state/${groupId}?versionId=${versionId}`;

		return getProductCatalogApi(useNewProductCatalog).patch(
			url,
			{
				disabledAt
			},
			{
				headers: {
					Authorization: jwtToken
				}
			}
		);
	},

	removeProductGroup(groupId, versionId, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).delete(`/product-groups/${groupId}?versionId=${versionId}`, {
			headers: {
				Authorization: jwtToken
			}
		});
	},

	insertProductConfiguration(configData, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).post(
			`/product-configurations`,
			configData,
			{
				headers: {
					Authorization: jwtToken
				}
			});
	},

	updateProductConfiguration({configId, configData}, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).put(
			`/product-configurations/${configId}`,
			configData,
			{
				headers: {
					Authorization: jwtToken
				}
			});
	},

	toggleProductConfigurationDisabledState({configId, versionId, disabledAt}, jwtToken, useNewProductCatalog = false) {
		const url = isNestBackend()
			? `/product-configurations/${configId}/disabled-state?versionId=${versionId}`
			: `/product-configurations-disabled-state/${configId}?versionId=${versionId}`;

		return getProductCatalogApi(useNewProductCatalog).patch(
			url,
			{
				disabledAt
			},
			{
				headers: {
					Authorization: jwtToken
				}
			}
		);
	},

	removeProductConfiguration(configId, versionId = 1, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).delete(`/product-configurations/${configId}?versionId=${versionId}`, {
			headers: {
				Authorization: jwtToken
			}
		});
	},

	updateAssortmentRulesByProduct({productId, shopIds}, jwtToken, useNewProductCatalog = false) {
		return getProductCatalogApi(useNewProductCatalog).put("/assortment-rules", {
			productId,
			shopIds
		}, {
			headers: {Authorization: jwtToken}

		});
	},

	async updateMassProducts(data, jwtToken) {
		return BifrostBackendNestApi.patch(`/products/mass-editing-products`, data, {
			headers: {
				Authorization: jwtToken
			}
		});
	}
});

export default ProductCatalogProvider;
